import type { Ref } from 'vue';

import { onBandwidthImprovement } from '~/composables/useHlsPlayer/shakaLoader';
import { resolveVariantUrls } from '~/composables/useVideoUrls';
import { ErrorCategory } from '~/core/ports/datadog';
import { useDatadog } from '~~/composables/useDatadog';

export function useIosQualityUpgrade(videoRef: Ref<HTMLVideoElement | null>) {
	const { trackError } = useDatadog();

	let variantUrls: string[] = [];
	let variantIndex = -1; // index into variantUrls; -1 = still on master
	let upgradeAbort: AbortController | null = null;
	let upgradeInterval: ReturnType<typeof setInterval> | null = null;
	let lastWaitingAt = 0;
	let prevBufferEnd = 0;
	let prevSampleTime = 0;
	let isUpgrading = false; // prevents concurrent doUpgrade calls from interval + probe

	function getBufferAhead(video: HTMLVideoElement): number {
		const ct = video.currentTime;
		for (let i = 0; i < video.buffered.length; i++) {
			if (ct >= video.buffered.start(i) && ct <= video.buffered.end(i)) {
				return video.buffered.end(i) - ct;
			}
		}
		return 0;
	}

	function stopUpgrade() {
		if (upgradeInterval) {
			clearInterval(upgradeInterval);
			upgradeInterval = null;
		}
	}

	async function doUpgrade(toIndex: number) {
		if (isUpgrading) return;
		isUpgrading = true;
		stopUpgrade();
		const video = videoRef.value;
		const nextUrl = variantUrls[toIndex];
		if (!video || !nextUrl) {
			isUpgrading = false;
			return;
		}

		const savedTime = video.currentTime;
		variantIndex = toIndex;
		video.src = nextUrl;
		video.load();

		// Wait for loadedmetadata before seeking — setting currentTime before this
		// event is silently ignored by AVPlayer, causing the video to restart from 0.
		// Race against error and abort so the promise always resolves and the video
		// element reference is not held indefinitely.
		await new Promise<void>(resolve => {
			// Both listeners must remove each other — if loadedmetadata fires,
			// the error listener would otherwise accumulate across upgrade calls.
			const cleanup = () => {
				video.removeEventListener('loadedmetadata', onMeta);
				video.removeEventListener('error', onErr);
			};
			const onMeta = () => {
				cleanup();
				resolve();
			};
			const onErr = () => {
				cleanup();
				resolve();
			};
			video.addEventListener('loadedmetadata', onMeta, { once: true });
			video.addEventListener('error', onErr, { once: true });
			if (upgradeAbort?.signal.aborted) {
				cleanup();
				resolve();
				return;
			}
			upgradeAbort?.signal.addEventListener(
				'abort',
				() => {
					cleanup();
					resolve();
				},
				{ once: true }
			);
		});

		if (!videoRef.value || upgradeAbort?.signal.aborted) {
			isUpgrading = false;
			return;
		}

		video.currentTime = savedTime;

		try {
			await video.play();
		} catch (err) {
			trackError({
				message: `[HlsVideoPlayer] iosDoUpgrade: play failed after quality switch: ${(err as Error)?.message}`,
				source: 'source',
				category: ErrorCategory.UI,
				stack: (err as Error)?.stack
			});
		} finally {
			isUpgrading = false;
		}

		// Resume monitoring in case a higher variant still exists
		startUpgrade();
	}

	function startUpgrade() {
		if (variantUrls.length === 0) return;
		stopUpgrade();

		prevSampleTime = Date.now();
		prevBufferEnd = 0;

		upgradeInterval = setInterval(() => {
			const video = videoRef.value;
			if (!video || video.paused) return;

			const now = Date.now();
			const bufferAhead = getBufferAhead(video);
			const bufferEnd = video.currentTime + bufferAhead;
			const elapsed = (now - prevSampleTime) / 1000;
			const grew = bufferEnd - prevBufferEnd;
			const growthRate = elapsed > 0 ? grew / elapsed : 0;

			prevBufferEnd = bufferEnd;
			prevSampleTime = now;

			const nextIndex = variantIndex + 1;
			if (nextIndex >= variantUrls.length) {
				stopUpgrade();
				return;
			}

			const secsSinceStall = (now - lastWaitingAt) / 1000;
			const networkStable =
				growthRate > 2.5 && // buffer filling 2.5× faster than playback
				bufferAhead > 5 && // ≥5s buffered ahead
				secsSinceStall > 10; // no stall in last 10s

			if (networkStable) void doUpgrade(nextIndex);
		}, 500);
	}

	function markWaiting() {
		lastWaitingAt = Date.now();
	}

	async function fetchVariants(masterUrl: string) {
		upgradeAbort?.abort();
		variantUrls = [];
		variantIndex = -1;
		prevBufferEnd = 0;
		prevSampleTime = 0;

		upgradeAbort = new AbortController();
		try {
			variantUrls = await resolveVariantUrls(masterUrl, upgradeAbort.signal);
		} catch (err) {
			if ((err as Error)?.name !== 'AbortError') {
				trackError({
					message: `[HlsVideoPlayer] iosFetchVariants failed: ${(err as Error)?.message}`,
					source: 'source',
					category: ErrorCategory.UI,
					stack: (err as Error)?.stack
				});
			}
			return;
		}

		// If canplay already fired before variants resolved, start the upgrade monitor now
		const video = videoRef.value;
		if (
			video &&
			variantUrls.length > 0 &&
			video.readyState >= 3 /* HAVE_FUTURE_DATA */
		) {
			startUpgrade();
		}
	}

	function abortUpgrade() {
		stopUpgrade();
		upgradeAbort?.abort();
	}

	function subscribeToProbe(): () => void {
		return onBandwidthImprovement(() => {
			// Probe detected network improvement — attempt next quality step now
			const nextIndex = variantIndex + 1;
			if (nextIndex < variantUrls.length && !videoRef.value?.paused) {
				void doUpgrade(nextIndex);
			}
		});
	}

	return {
		fetchVariants,
		startUpgrade,
		stopUpgrade,
		markWaiting,
		abortUpgrade,
		subscribeToProbe
	};
}
